import React, { useEffect, useState } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';

import answerControlSx from './answerControlSx';
import copyAnswer from './copyAnswer';

const COPY_FEEDBACK_MS = 1_500;

type CopyStatus = 'idle' | 'copied' | 'failed';

const TOOLTIP_TITLES: Record<CopyStatus, string> = {
  idle: 'Copy',
  copied: 'Copied',
  failed: "Couldn't copy",
};

const VISUALLY_HIDDEN_SX = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  p: 0,
  m: '-1px',
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

const PinguCopyAnswer = ({
  getAnswerElement,
  disabled = false,
}: {
  getAnswerElement: () => HTMLElement | null;
  disabled?: boolean;
}) => {
  const [status, setStatus] = useState<CopyStatus>('idle');

  useEffect(() => {
    if (status === 'idle') return undefined;
    const timer = window.setTimeout(() => setStatus('idle'), COPY_FEEDBACK_MS);
    return () => window.clearTimeout(timer);
  }, [status]);

  const handleCopy = async () => {
    const answer = getAnswerElement();
    if (!answer) return;

    const copied = await copyAnswer(
      navigator.clipboard,
      { html: answer.innerHTML, text: answer.innerText },
      typeof window.ClipboardItem === 'function' ? window.ClipboardItem : undefined,
    );
    setStatus(copied ? 'copied' : 'failed');
  };

  return (
    // Anchors the hidden status here, or it escapes the transcript and stretches the panel.
    <Box component="span" sx={{ position: 'relative', display: 'inline-flex' }}>
      <Tooltip title={TOOLTIP_TITLES[status]}>
        <span>
          <IconButton
            size="small"
            aria-label="Copy answer"
            onClick={handleCopy}
            disabled={disabled}
            sx={answerControlSx}
          >
            {status === 'copied'
              ? <CheckOutlinedIcon fontSize="small" />
              : <ContentCopyOutlinedIcon fontSize="small" />}
          </IconButton>
        </span>
      </Tooltip>
      <Box component="span" role="status" sx={VISUALLY_HIDDEN_SX}>
        {status === 'idle' ? '' : TOOLTIP_TITLES[status]}
      </Box>
    </Box>
  );
};

export default PinguCopyAnswer;
